import { gql } from '@apollo/client'
import { DoctrineMetadata, JurisprudenceMetadata } from '@jusbrasil-web/shared-apollo-client/types'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import { ReactNode, useEffect, useRef, useState } from 'react'

import { BOX_MODAL_FRAME_BUS_ID } from '../../frame-bus'
import { CopyContentTriggerUtil } from '../../functions/copy-content-trigger-util/copy-content-trigger-util'
import { useBoxLockIframe } from '../../hooks'
import { BoxUniqueFeatureModalProduct } from '../../types'
import {
  CopyContentTriggerStandalone_DocumentFragment as Document,
  CopyContentTriggerStandalone_HaystackItemFragment as HaystackItem,
  CopyContentTriggerStandalone_ViewerFragment,
} from './__generated__/copy-content-trigger-standalone.graphql'
import styles from './copy-content-trigger-standalone.module.scss'

export interface CopyContentTriggerStandaloneProps {
  appId: string
  children: ReactNode
  onCopySuccess: (defaultMessage: string, options?: { id?: string }) => void
  className?: string
  documentOrHaystackItem: Document | HaystackItem
  eventNamespace: string
  product?: BoxUniqueFeatureModalProduct
  featureCode: string
  ga4EventData?: {
    componentClass: string
    componentName?: string
    copyFeatureName?: string
  }
  ssrEventsProperties?: Record<string, unknown>
  viewer: CopyContentTriggerStandalone_ViewerFragment | null
  iframeUrl?: string

  // used to determine whether the box modal should be loaded. either in an iframe or not.
  disabled?: boolean

  // internal props used for Farol component version
  onVisibleChange?: (visible: boolean) => void
}

function isDocument(
  documentOrHaystackItem: Document | HaystackItem
): documentOrHaystackItem is Document {
  if ((documentOrHaystackItem as Document).docId) {
    return true
  }
  return false
}

function getDocumentData(documentOrHaystackItem: Document | HaystackItem) {
  if (isDocument(documentOrHaystackItem)) {
    const document = documentOrHaystackItem as Document
    return { kind: document.artifact, docId: document.docId }
  }
  const { kind, data } = documentOrHaystackItem as HaystackItem
  const { docId } = data as JurisprudenceMetadata | DoctrineMetadata
  return { kind: kind, docId }
}

let isBoxUniqueFeatureModalStandaloneImported = false

export function CopyContentTriggerStandalone(props: CopyContentTriggerStandaloneProps) {
  const {
    appId,
    children,
    className,
    eventNamespace,
    product,
    featureCode,
    ssrEventsProperties,
    onCopySuccess,
    viewer,
    documentOrHaystackItem,
    iframeUrl,
    ga4EventData,
    disabled = false,
    onVisibleChange,
  } = props
  const { kind, docId } = getDocumentData(documentOrHaystackItem)

  const [shouldEnableIframe, setShouldEnableIframe] = useState<boolean | null>(null)
  const elementRef = useRef<HTMLDivElement>(null)

  const { sendGA4Event } = useGA4Dispatcher()
  const fromComponent = 'DocumentSnippet' // [FIXME] should be passed as prop? :( @andersonba

  const iframed = !!iframeUrl
  const iframedEnabled = iframed && !disabled && shouldEnableIframe === true

  const boxIframe = useBoxLockIframe(
    BOX_MODAL_FRAME_BUS_ID,
    iframed ? iframeUrl : 'about:blank',
    {
      appId,
      featureCode,
      fromComponent,
      boxOptions: {},
      modalType: CopyContentTriggerUtil.boxModalType,
    },
    {
      prefetch: false,
      disabled: !iframedEnabled,
    }
  )

  useEffect(() => {
    if (disabled) return
    if (!iframed && !isBoxUniqueFeatureModalStandaloneImported) {
      isBoxUniqueFeatureModalStandaloneImported = true

      // See https://github.com/jusbrasil/box-js/tree/master/packages/box-unique-feature-modal-standalone
      // @ts-expect-error this module does not export anything, just alters window object
      import('@jusbrasil/box-unique-feature-modal-standalone')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, iframed])

  useEffect(() => {
    if (disabled) return
    if (viewer && elementRef.current) {
      const iframedOnlyConfig = {
        showBoxLockModal() {
          // empty function:
          // just to ignore `BoxModal is not loaded` error
        },
      }
      const copyContentTriggerUtil = new CopyContentTriggerUtil({
        window: boxIframe.frame.ref.current?.contentWindow || window,
        element: elementRef.current,
        eventData: {
          from_component: fromComponent,
          ...ssrEventsProperties,
          app_id: appId,
          feature_code: featureCode,
        },
        ga4EventData,
        eventNamespace,
        product,
        sendGA4Event,
        featureCode,
        loadBoxModalData: false,
        onVisibleChange(visible) {
          if (onVisibleChange) {
            onVisibleChange(visible)
          } else if (visible) {
            boxIframe.open()
          } else {
            boxIframe.close()
          }
        },
        onLoadViewerCanUseFeature(viewerCanUseFeature) {
          // this state is only used with iframed mode, so, it prevents re-rendering when not needed
          if (iframed) {
            setShouldEnableIframe(!viewerCanUseFeature)
          }
        },
        onCopySuccess() {
          onCopySuccess('Conteúdo copiado!', {
            id: 'copy-content-trigger-standalone-toast',
          })
        },
        ...(iframed ? iframedOnlyConfig : {}),
      })
      copyContentTriggerUtil.addCopyListener()

      return () => {
        copyContentTriggerUtil.removeCopyListener()
      }
    }
  }, [
    disabled,
    appId,
    eventNamespace,
    product,
    featureCode,
    sendGA4Event,
    ssrEventsProperties,
    viewer,
    onCopySuccess,
    boxIframe,
    ga4EventData,
    iframed,
    onVisibleChange,
  ])

  return (
    <div className={className} ref={elementRef} data-doc-artifact={kind} data-doc-id={docId}>
      {children}
      {iframedEnabled && boxIframe.loading && (
        <div className={styles.loading}>
          <div className={styles.spinner}>
            <div />
            <div />
            <div />
            <div />
          </div>
          Copiando...
        </div>
      )}
    </div>
  )
}

CopyContentTriggerStandalone.fragments = {
  document: gql`
    fragment CopyContentTriggerStandalone_document on Document {
      artifact
      docId
    }
  `,

  haystackItem: gql`
    fragment CopyContentTriggerStandalone_haystackItem on HaystackItem {
      kind
      data {
        ... on JurisprudenceMetadata {
          docId
        }

        ... on DoctrineMetadata {
          docId
        }
      }
    }
  `,

  viewer: gql`
    fragment CopyContentTriggerStandalone_viewer on Profile {
      pid
    }
  `,
}
